import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import { MAKALE_ICERIKLERI } from './yardim/YardimIcerik';
import { useTranslation } from 'react-i18next';

interface Talep {
    talepID: number;
    konu: string;
    aciklama?: string;
    kullaniciID: number;
    kullaniciAdi?: string;
    olusturmaTarihi: string;
    durum: number;
    merkeziTalepID?: string;
    sonMerkeziCevapTarihi?: string;
    okunmamisCevap: boolean;
}

interface Mesaj {
    mesajID: number;
    talepID: number;
    mesaj: string;
    gonderenTur: number;
    gonderenAd?: string;
    tarih: string;
    okunduMu: boolean;
}

interface YardimMakale {
    baslik: string;
    slug: string;
}

interface YardimKategori {
    baslik: string;
    icon: string;
    makaleler: YardimMakale[];
}

// ─── Yardım bileşeni ────────────────────────────────────────────────────────

interface CmsKategori {
    kategoriID: number; kategoriKodu: string; ikonSinifi: string | null;
    baslik: string; sayfaSayisi: number;
}
interface CmsSayfa {
    sayfaID: number; sayfaSlug: string; baslik: string; ikonSinifi: string | null;
}

const YardimBolumu: React.FC = () => {
    const { t, i18n } = useTranslation();
    const dilKodu = i18n.language?.split('-')[0] || 'tr';

    const [kategoriler, setKategoriler] = useState<CmsKategori[]>([]);
    const [sayfaMap, setSayfaMap]       = useState<Record<number, CmsSayfa[]>>({});
    const [dbIcerik, setDbIcerik]       = useState<string | null | false>(null); // false = yükleniyor
    const [acikKatID, setAcikKatID]     = useState<number | null>(null);
    const [aktifMakale, setAktifMakale] = useState<{ slug: string; baslik: string; katBaslik: string } | null>(null);
    const [katYukleniyor, setKatYukleniyor] = useState(true);

    useEffect(() => {
        axiosInstance.get(`/api/DestekCMS/kategoriler?dilKodu=${dilKodu}`)
            .then(r => {
                setKategoriler(r.data);
                if (r.data.length > 0) {
                    setAcikKatID(r.data[0].kategoriID);
                }
            })
            .catch(() => setKategoriler([]))
            .finally(() => setKatYukleniyor(false));
    }, [dilKodu]);

    useEffect(() => {
        if (acikKatID === null) return;
        if (sayfaMap[acikKatID]) return;
        const kat = kategoriler.find(k => k.kategoriID === acikKatID);
        if (!kat) return;
        axiosInstance.get(`/api/DestekCMS/sayfalar/${kat.kategoriKodu}?dilKodu=${dilKodu}`)
            .then(r => setSayfaMap(prev => ({ ...prev, [acikKatID]: r.data })))
            .catch(() => setSayfaMap(prev => ({ ...prev, [acikKatID]: [] })));
    }, [acikKatID, dilKodu]);

    const handleMakaleAc = async (slug: string, baslik: string, katBaslik: string) => {
        setAktifMakale({ slug, baslik, katBaslik });
        setDbIcerik(false);
        try {
            const r = await axiosInstance.get(`/api/DestekCMS/icerik?slug=${encodeURIComponent(slug)}&dilKodu=${dilKodu}`);
            setDbIcerik(r.data?.icerik ?? null);
        } catch {
            setDbIcerik(null);
        }
    };

    return (
        <div style={{ display: 'flex', height: '100%' }}>
            {/* Sol — Kategori & Makale listesi */}
            <div style={{ width: 280, flexShrink: 0, borderRight: '1px solid #e5e7eb', overflowY: 'auto', background: '#fafafa' }}>
                <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', letterSpacing: '.4px', textTransform: 'uppercase' }}>Yardım Konuları</div>
                </div>
                {katYukleniyor && (
                    <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                        <i className="fas fa-spinner fa-spin" /> Yükleniyor...
                    </div>
                )}
                {!katYukleniyor && kategoriler.map(kat => {
                    const acik = acikKatID === kat.kategoriID;
                    const sayfalar = sayfaMap[kat.kategoriID] ?? [];
                    return (
                        <div key={kat.kategoriID}>
                            <button
                                onClick={() => setAcikKatID(acik ? null : kat.kategoriID)}
                                style={{
                                    width: '100%', padding: '10px 16px',
                                    background: acik ? '#fff8f0' : 'transparent',
                                    border: 'none', borderBottom: '1px solid #f3f4f6',
                                    display: 'flex', alignItems: 'center', gap: 9,
                                    cursor: 'pointer', textAlign: 'left',
                                    color: acik ? '#cd6906' : '#374151',
                                    fontWeight: acik ? 700 : 500, fontSize: 13,
                                    transition: 'all .12s',
                                }}
                            >
                                <i className={`fas ${kat.ikonSinifi?.replace('fas ', '') ?? 'fa-folder'}`} style={{ width: 16, textAlign: 'center', fontSize: 13, opacity: .7 }} />
                                <span style={{ flex: 1 }}>{kat.baslik}</span>
                                <i className={`fas fa-chevron-${acik ? 'down' : 'right'}`} style={{ fontSize: 10, opacity: .5 }} />
                            </button>
                            {acik && (
                                <div style={{ background: '#fff', borderBottom: '1px solid #f3f4f6' }}>
                                    {sayfalar.length === 0 && (
                                        <div style={{ padding: '10px 16px 10px 42px', fontSize: 12, color: '#9ca3af' }}>
                                            <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />
                                        </div>
                                    )}
                                    {sayfalar.map(s => {
                                        const secili = aktifMakale?.slug === s.sayfaSlug;
                                        return (
                                            <button
                                                key={s.sayfaSlug}
                                                onClick={() => handleMakaleAc(s.sayfaSlug, s.baslik, kat.baslik)}
                                                style={{
                                                    width: '100%', padding: '8px 16px 8px 42px',
                                                    background: secili ? '#fff8f0' : 'transparent',
                                                    border: 'none', display: 'flex', alignItems: 'center', gap: 7,
                                                    cursor: 'pointer', textAlign: 'left',
                                                    color: secili ? '#cd6906' : '#4b5563',
                                                    fontWeight: secili ? 600 : 400, fontSize: 12.5,
                                                    borderLeft: secili ? '3px solid #cd6906' : '3px solid transparent',
                                                    transition: 'all .1s',
                                                }}
                                            >
                                                <i className="fas fa-file-alt" style={{ fontSize: 11, opacity: .45, flexShrink: 0 }} />
                                                {s.baslik}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Sağ — Makale içeriği */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
                {aktifMakale ? (
                    <div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 8, fontWeight: 500 }}>
                            {aktifMakale.katBaslik} &rsaquo; {aktifMakale.baslik}
                        </div>
                        <h2 style={{ margin: '0 0 24px', fontSize: 20, fontWeight: 700, color: '#111827', paddingBottom: 14, borderBottom: '2px solid #f1f5f9' }}>
                            {aktifMakale.baslik}
                        </h2>
                        {dbIcerik === false ? (
                            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
                                <i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }} />
                            </div>
                        ) : dbIcerik ? (
                            <div
                                className="yardim-icerik"
                                style={{ fontSize: 14, lineHeight: 1.75, color: '#374151' }}
                                dangerouslySetInnerHTML={{ __html: dbIcerik }}
                            />
                        ) : (
                            MAKALE_ICERIKLERI[aktifMakale.slug] ?? (
                                <div style={{
                                    background: '#f9fafb', border: '1px dashed #e5e7eb',
                                    borderRadius: 10, padding: '32px 28px',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                                    color: '#9ca3af', textAlign: 'center'
                                }}>
                                    <i className="fas fa-pen-nib" style={{ fontSize: 28, color: '#d1d5db' }} />
                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>Bu makale hazırlanıyor</div>
                                    <div style={{ fontSize: 13 }}>İçerik yakında eklenecek.</div>
                                </div>
                            )
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 14, color: '#9ca3af', textAlign: 'center', paddingTop: 40 }}>
                        <i className="fas fa-book-open" style={{ fontSize: 40, color: '#e5e7eb' }} />
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#6b7280' }}>{t('destek.yardimMerkezi')}</div>
                        <div style={{ fontSize: 13, maxWidth: 320 }}>
                            Soldan bir konu başlığını genişletin ve ilgilendiğiniz makaleyi seçin.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Ana Destek sayfası ──────────────────────────────────────────────────────

const Destek: React.FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [anaTab, setAnaTab] = useState<'talepler' | 'yardim'>('talepler');
    const [talepler, setTalepler] = useState<Talep[]>([]);
    const [aktifTalep, setAktifTalep] = useState<Talep | null>(null);
    const [mesajlar, setMesajlar] = useState<Mesaj[]>([]);
    const [loading, setLoading] = useState(false);
    const [mesajYukleniyor, setMesajYukleniyor] = useState(false);
    const [yeniTalepAcik, setYeniTalepAcik] = useState(false);
    const [konu, setKonu] = useState('');
    const [aciklama, setAciklama] = useState('');
    const [yeniMesaj, setYeniMesaj] = useState('');
    const [gonderiyor, setGonderiyor] = useState(false);
    const [kayit, setKayit] = useState(false);
    const mesajSonuRef = useRef<HTMLDivElement>(null);

    useEffect(() => { if (anaTab === 'talepler') fetchTalepler(); }, [anaTab]);

    useEffect(() => {
        if (aktifTalep) fetchMesajlar(aktifTalep.talepID);
    }, [aktifTalep?.talepID]);

    useEffect(() => {
        mesajSonuRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mesajlar]);

    const fetchTalepler = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/Destek?kullaniciId=${user?.kullaniciID}`);
            setTalepler(res.data);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setLoading(false);
        }
    };

    const fetchMesajlar = async (talepId: number) => {
        setMesajYukleniyor(true);
        try {
            const res = await axiosInstance.get(`/api/Destek/${talepId}/mesajlar`);
            setMesajlar(res.data);
            setTalepler(prev => prev.map(item => item.talepID === talepId ? { ...t, okunmamisCevap: false } : t));
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setMesajYukleniyor(false);
        }
    };

    const handleTalepSec = (talep: Talep) => {
        setAktifTalep(talep);
        setYeniTalepAcik(false);
    };

    const handleYeniTalep = async () => {
        if (!konu.trim()) { toast.error(t('msg.zorunluAlanlar')); return; }
        if (!aciklama.trim()) { toast.error(t('msg.zorunluAlanlar')); return; }
        setKayit(true);
        try {
            const res = await axiosInstance.post('/api/Destek', {
                konu: konu.trim(),
                aciklama: aciklama.trim(),
                kullaniciID: user?.kullaniciID
            });
            toast.success(t('msg.islemBasarili'));
            setKonu('');
            setAciklama('');
            setYeniTalepAcik(false);
            const talepRes = await axiosInstance.get(`/api/Destek?kullaniciId=${user?.kullaniciID}`);
            setTalepler(talepRes.data);
            const yeni = talepRes.data.find((t: Talep) => t.talepID === res.data.talepID);
            if (yeni) setAktifTalep(yeni);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setKayit(false);
        }
    };

    const handleMesajGonder = async () => {
        if (!yeniMesaj.trim() || !aktifTalep) return;
        setGonderiyor(true);
        try {
            await axiosInstance.post(`/api/Destek/${aktifTalep.talepID}/mesaj`, { mesaj: yeniMesaj.trim() });
            setYeniMesaj('');
            await fetchMesajlar(aktifTalep.talepID);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setGonderiyor(false);
        }
    };

    const handleKapat = async (talepId: number) => {
        if (!window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.post(`/api/Destek/${talepId}/kapat`);
            toast.success(t('msg.islemBasarili'));
            await fetchTalepler();
            if (aktifTalep?.talepID === talepId)
                setAktifTalep(prev => prev ? { ...prev, durum: 2 } : null);
        } catch {
            toast.error(t('msg.islemHatasi'));
        }
    };

    const formatTarih = (tarih: string) =>
        new Date(tarih).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const acikSayi = talepler.filter(item => item.durum === 1).length;
    const okunmamisSayi = talepler.filter(item => item.okunmamisCevap).length;

    return (
        <>
            <style>{`
                .dst-wrap { display:flex; gap:0; height:calc(100vh - 175px); min-height:500px; background:#fff; border:1px solid #e5e7eb; border-radius:0 14px 14px 14px; overflow:hidden; font-family:'Inter',-apple-system,sans-serif; }
                .dst-sidebar { width:280px; flex-shrink:0; border-right:1px solid #e5e7eb; display:flex; flex-direction:column; background:#fafafa; }
                .dst-sidebar-head { padding:18px 16px 14px; border-bottom:1px solid #e5e7eb; }
                .dst-sidebar-head h3 { margin:0 0 12px; font-size:15px; font-weight:700; color:#111827; }
                .dst-new-btn { width:100%; padding:9px; border:none; border-radius:9px; background:#cd6906; color:#fff; font-size:13px; font-weight:600; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px; transition:background .15s; }
                .dst-new-btn:hover { background:#a85505; }
                .dst-list { flex:1; overflow-y:auto; padding:8px; }
                .dst-item { padding:12px; border-radius:9px; cursor:pointer; margin-bottom:4px; border:1.5px solid transparent; transition:all .15s; position:relative; }
                .dst-item:hover { background:#f3f4f6; }
                .dst-item.aktif { background:#fff8f0; border-color:#cd6906; }
                .dst-item-konu { font-size:13px; font-weight:600; color:#111827; margin-bottom:4px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; padding-right:20px; }
                .dst-item-meta { font-size:11px; color:#9ca3af; }
                .dst-item-badge { position:absolute; top:10px; right:10px; width:8px; height:8px; border-radius:50%; background:#ef4444; }
                .dst-durum { display:inline-block; padding:2px 8px; border-radius:12px; font-size:10px; font-weight:600; }
                .dst-durum-acik { background:#d1fae5; color:#065f46; }
                .dst-durum-kapali { background:#f3f4f6; color:#6b7280; }
                .dst-main { flex:1; display:flex; flex-direction:column; min-width:0; }
                .dst-main-head { padding:16px 24px; border-bottom:1px solid #e5e7eb; display:flex; align-items:center; justify-content:space-between; gap:12px; }
                .dst-main-konu { font-size:15px; font-weight:700; color:#111827; }
                .dst-main-meta { font-size:12px; color:#9ca3af; margin-top:2px; }
                .dst-kapat-btn { padding:7px 14px; border:1px solid #e5e7eb; border-radius:8px; background:#fff; color:#374151; font-size:12px; font-weight:600; cursor:pointer; white-space:nowrap; }
                .dst-kapat-btn:hover { background:#fee2e2; border-color:#fca5a5; color:#dc2626; }
                .dst-mesajlar { flex:1; overflow-y:auto; padding:20px 24px; display:flex; flex-direction:column; gap:14px; }
                .dst-baloncuk { max-width:72%; display:flex; flex-direction:column; gap:4px; }
                .dst-baloncuk.kullanici { align-self:flex-end; align-items:flex-end; }
                .dst-baloncuk.sistem { align-self:flex-start; align-items:flex-start; }
                .dst-baloncuk-icerik { padding:11px 15px; border-radius:14px; font-size:13.5px; line-height:1.55; word-break:break-word; }
                .dst-baloncuk.kullanici .dst-baloncuk-icerik { background:#cd6906; color:#fff; border-bottom-right-radius:4px; }
                .dst-baloncuk.sistem .dst-baloncuk-icerik { background:#f1f5f9; color:#1e293b; border-bottom-left-radius:4px; }
                .dst-baloncuk-meta { font-size:11px; color:#9ca3af; }
                .dst-input-area { padding:14px 20px; border-top:1px solid #e5e7eb; display:flex; gap:10px; align-items:flex-end; }
                .dst-textarea { flex:1; padding:10px 14px; border:1.5px solid #e2e8f0; border-radius:10px; font-size:13.5px; font-family:inherit; resize:none; outline:none; min-height:42px; max-height:120px; line-height:1.5; transition:border-color .15s; }
                .dst-textarea:focus { border-color:#cd6906; }
                .dst-gonder-btn { padding:10px 18px; background:#cd6906; color:#fff; border:none; border-radius:10px; font-size:13px; font-weight:600; cursor:pointer; white-space:nowrap; height:42px; transition:background .15s; }
                .dst-gonder-btn:hover:not(:disabled) { background:#a85505; }
                .dst-gonder-btn:disabled { opacity:.55; cursor:not-allowed; }
                .dst-empty { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; color:#9ca3af; }
                .dst-empty p { font-size:14px; margin:0; }
                .dst-yeni-form { padding:20px 24px; display:flex; flex-direction:column; gap:14px; max-width:600px; }
                .dst-field label { display:block; font-size:11.5px; font-weight:600; color:#64748b; margin-bottom:6px; letter-spacing:.3px; }
                .dst-field input, .dst-field textarea { width:100%; padding:10px 14px; border:1.5px solid #e2e8f0; border-radius:9px; font-size:14px; font-family:inherit; outline:none; box-sizing:border-box; }
                .dst-field input:focus, .dst-field textarea:focus { border-color:#cd6906; }
                .dst-field textarea { resize:vertical; min-height:120px; line-height:1.55; }
                .dst-form-btns { display:flex; gap:10px; }
                .dst-iptal-btn { padding:9px 18px; border:1.5px solid #e2e8f0; border-radius:8px; background:#fff; color:#374151; font-size:13px; font-weight:600; cursor:pointer; }
                .dst-kaydet-btn { padding:9px 22px; background:#cd6906; color:#fff; border:none; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; }
                .dst-kaydet-btn:disabled { opacity:.55; cursor:not-allowed; }
                .dst-page-head { padding:0 0 16px; display:flex; align-items:center; justify-content:space-between; }
                .dst-page-title { font-size:20px; font-weight:700; color:#111827; margin:0; }
                .dst-stats { display:flex; gap:8px; }
                .dst-stat { background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:6px 14px; font-size:12px; font-weight:600; color:#374151; }
                .dst-stat.warn { background:#fef3c7; border-color:#fde68a; color:#92400e; }
                /* Ana tab bar */
                .dst-ana-tabs { display:flex; gap:0; border-bottom:0; margin-bottom:0; }
                .dst-ana-tab { padding:10px 22px; border:1px solid #e5e7eb; background:#f9fafb; color:#6b7280; font-size:13px; font-weight:600; cursor:pointer; border-bottom:none; border-radius:10px 10px 0 0; margin-right:4px; transition:all .15s; font-family:inherit; }
                .dst-ana-tab.aktif { background:#fff; color:#111827; border-bottom:1px solid #fff; position:relative; z-index:1; }
                .dst-ana-tab:hover:not(.aktif) { background:#f3f4f6; }
            `}</style>

            <div style={{ padding: '24px 24px 0', fontFamily: "'Inter',-apple-system,sans-serif" }}>
                <div className="dst-page-head">
                    <h1 className="dst-page-title">Destek & Yardım</h1>
                    {anaTab === 'talepler' && (
                        <div className="dst-stats">
                            <span className="dst-stat">Açık: {acikSayi}</span>
                            {okunmamisSayi > 0 && <span className="dst-stat warn">{okunmamisSayi} yeni cevap</span>}
                        </div>
                    )}
                </div>

                {/* Ana sekmeler */}
                <div className="dst-ana-tabs">
                    <button className={`dst-ana-tab${anaTab === 'talepler' ? ' aktif' : ''}`} onClick={() => setAnaTab('talepler')}>
                        <i className="fas fa-headset" style={{ marginRight: 7 }} />
                        Destek Taleplerim
                    </button>
                    <button className={`dst-ana-tab${anaTab === 'yardim' ? ' aktif' : ''}`} onClick={() => setAnaTab('yardim')}>
                        <i className="fas fa-book-open" style={{ marginRight: 7 }} />
                        Yardım
                    </button>
                </div>
            </div>

            <div style={{ padding: '0 24px 24px' }}>
                {/* Destek Taleplerim */}
                {anaTab === 'talepler' && (
                    <div className="dst-wrap">
                        {/* Sidebar */}
                        <div className="dst-sidebar">
                            <div className="dst-sidebar-head">
                                <h3>Taleplerim</h3>
                                <button className="dst-new-btn" onClick={() => { setYeniTalepAcik(true); setAktifTalep(null); }}>
                                    <i className="fas fa-plus" style={{ fontSize: 12 }} />
                                    Yeni Talep
                                </button>
                            </div>
                            <div className="dst-list">
                                {loading ? (
                                    <div style={{ padding: 16, color: '#9ca3af', fontSize: 13 }}>{t('msg.yukleniyor')}</div>
                                ) : talepler.length === 0 ? (
                                    <div style={{ padding: 16, color: '#9ca3af', fontSize: 13 }}>Henüz talep yok.</div>
                                ) : talepler.map(item => (
                                    <div key={item.talepID}
                                        className={`dst-item${aktifTalep?.talepID === item.talepID ? ' aktif' : ''}`}
                                        onClick={() => handleTalepSec(item)}
                                    >
                                        {item.okunmamisCevap && <div className="dst-item-badge" />}
                                        <div className="dst-item-konu">{item.konu}</div>
                                        <div className="dst-item-meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span className={`dst-durum ${item.durum === 1 ? 'dst-durum-acik' : 'dst-durum-kapali'}`}>
                                                {item.durum === 1 ? 'Açık' : 'Tamamlandı'}
                                            </span>
                                            <span>{new Date(item.olusturmaTarihi).toLocaleDateString('tr-TR')}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Ana alan */}
                        <div className="dst-main">
                            {yeniTalepAcik ? (
                                <>
                                    <div className="dst-main-head">
                                        <div>
                                            <div className="dst-main-konu">Yeni Destek Talebi</div>
                                            <div className="dst-main-meta">Talebinizi ayrıntılı açıklayın</div>
                                        </div>
                                    </div>
                                    <div className="dst-yeni-form">
                                        <div className="dst-field">
                                            <label>Konu *</label>
                                            <input type="text" placeholder="Talebinizin konusu" value={konu} onChange={e => setKonu(e.target.value)} maxLength={200} />
                                        </div>
                                        <div className="dst-field">
                                            <label>Açıklama *</label>
                                            <textarea placeholder={t('label.ara')} value={aciklama} onChange={e => setAciklama(e.target.value)} />
                                        </div>
                                        <div className="dst-form-btns">
                                            <button className="dst-iptal-btn" onClick={() => setYeniTalepAcik(false)}>{t('btn.iptal')}</button>
                                            <button className="dst-kaydet-btn" onClick={handleYeniTalep} disabled={kayit}>
                                                {kayit ? 'Gönderiliyor...' : 'Talebi Oluştur'}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : aktifTalep ? (
                                <>
                                    <div className="dst-main-head">
                                        <div style={{ minWidth: 0 }}>
                                            <div className="dst-main-konu" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                #{aktifTalep.talepID} — {aktifTalep.konu}
                                            </div>
                                            <div className="dst-main-meta">
                                                {formatTarih(aktifTalep.olusturmaTarihi)}
                                                {' · '}
                                                <span className={`dst-durum ${aktifTalep.durum === 1 ? 'dst-durum-acik' : 'dst-durum-kapali'}`}>
                                                    {aktifTalep.durum === 1 ? 'Açık' : 'Tamamlandı'}
                                                </span>
                                            </div>
                                        </div>
                                        {aktifTalep.durum === 1 && (
                                            <button className="dst-kapat-btn" onClick={() => handleKapat(aktifTalep.talepID)}>Talebi Kapat</button>
                                        )}
                                    </div>
                                    <div className="dst-mesajlar">
                                        {mesajYukleniyor ? (
                                            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', paddingTop: 20 }}>{t('msg.yukleniyor')}</div>
                                        ) : mesajlar.length === 0 ? (
                                            <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', paddingTop: 20 }}>Henüz mesaj yok.</div>
                                        ) : mesajlar.map(m => (
                                            <div key={m.mesajID} className={`dst-baloncuk ${m.gonderenTur === 1 ? 'kullanici' : 'sistem'}`}>
                                                <div className="dst-baloncuk-icerik">{m.mesaj}</div>
                                                <div className="dst-baloncuk-meta">
                                                    {m.gonderenTur === 2 && <strong style={{ color: '#6366f1' }}>{m.gonderenAd || 'Destek Ekibi'} · </strong>}
                                                    {formatTarih(m.tarih)}
                                                </div>
                                            </div>
                                        ))}
                                        <div ref={mesajSonuRef} />
                                    </div>
                                    {aktifTalep.durum === 1 && (
                                        <div className="dst-input-area">
                                            <textarea
                                                className="dst-textarea"
                                                placeholder={t('label.ara')}
                                                value={yeniMesaj}
                                                onChange={e => setYeniMesaj(e.target.value)}
                                                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleMesajGonder(); } }}
                                                rows={1}
                                            />
                                            <button className="dst-gonder-btn" onClick={handleMesajGonder} disabled={gonderiyor || !yeniMesaj.trim()}>
                                                {gonderiyor ? '...' : 'Gönder'}
                                            </button>
                                        </div>
                                    )}
                                    {aktifTalep.durum === 2 && (
                                        <div style={{ padding: '12px 20px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                                            Bu talep tamamlandı. Yeni bir sorun için yeni talep açın.
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="dst-empty">
                                    <i className="fas fa-comments" style={{ fontSize: 40, color: '#e5e7eb' }} />
                                    <p>Sol taraftan bir talep seçin</p>
                                    <p style={{ fontSize: 12 }}>veya yeni talep oluşturun</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Yardım */}
                {anaTab === 'yardim' && (
                    <div className="dst-wrap">
                        <YardimBolumu />
                    </div>
                )}
            </div>
        </>
    );
};

export default Destek;
